<template>
  <div :class="$style.shared_board_page">
    <shared-header
      :gantt-instance="{}"
    />
    <div :class="$style.shared_board_view">
      <board-view
        :shared-view="true"
        :tasks-map="tasksMap"
        :project-resources="projectResources"
        :columns="columns"
        :col-width="colWidth"
        :show-filter-stub="showFilterStub"
        :collapsed="collapsed"
      />
      <div :class="$style.filter_box">
        <portal-target name="portal-filter-sidebar" />
      </div>
    </div>
  </div>
</template>

<script>
import log from '$$log';
import BoardView from './components/BoardView.vue';
import SharedHeader from '../sharedViews/sharedHeader/SharedHeader.vue';
import dateHelper from '$$helpers/dateFormats';
import { checkPricingAccess } from '$$helpers/pricingHelper';
import globalStore from '$$globalStore';

import {
  mapTaskForSharedBoard,
  compareTasks,
  taskToOptions,
} from '../../helpers/boardHelpers';
import calendarModule from '../../components/calendar';
import customHelper from '../../helpers/custom';
import ganttFilterHelper from '../../helpers/gantt_filter';
import rights from '../../components/rights';

export default {
  name: 'SharedBoardPage',
  components: {
    BoardView,
    SharedHeader,
  },
  provide() {
    return {
      getTasksMap: this.getTasksMap,
      getProjectName: this.getProjectName,
      getCalendarConfig: this.getCalendarConfig,
      isFieldDisabled: this.isFieldDisabledForSharedBoard,
      customHelper,
      closeContextMenus: () => {},
      hideResourcePopup: () => {},
      toggleContextMenu: () => {},
      hideFilterPopup: () => {},
      hideProjectSettingsPopup: () => {},
      tippyBoundary: () => {},
    };
  },
  data() {
    return {
      tasksMap: {},
      projectResources: [],
      columns: [],
      colWidth: 324,
      showFilterStub: false,
      collapsed: {},
      projectId: 0,
      boardTasks: [],
      customSort: [],
    };
  },
  computed: {
    currentFilter() {
      return globalStore.getters['filter/currentFilter'];
    },
    filterTasksCallback() {
      return ganttFilterHelper.getFilter({
        options: this.currentFilter?.properties,
        value: this.currentFilter?.properties.text || '',
      }, null);
    },
    boardConfig() {
      return globalStore.getters['board/currentConfig'];
    },
  },
  watch: {
    currentFilter(val) {
      if (!val || val.type !== 'sharedBoard') return;

      this.prepareGroupedTasks();
    },
  },
  created() {
    this.initData();

    this.$nextTick(() => {
      this.initBoard();
    });
  },
  methods: {
    initData() {
      this.projectId = globalStore.getters['board/projectId'];
      this.projectResources = globalStore.getters['resourcesModel/getResourcesByProjectId'](globalStore.getters['board/projectId']).reduce((acc, res) => {
        acc[res.id] = res;

        return acc;
      }, {});
      this.calendarConfig = calendarModule.getCalendarConfig(globalStore.getters['board/projectId']);
    },
    initBoard() {
      const t = log.time('viewBoard.shared.init');

      this.initCollapsedState();
      this.initProjectTasks();

      this.$nextTick(() => {
        this.prepareGroupedTasks();
        t.end();
      });
    },
    initCollapsedState() {
      this.collapsed = (this.boardConfig.grouping?.[0]?.order || []).reduce((acc, column) => {
        acc[column.id] = column.isCollapsed || false;

        return acc;
      }, {});
    },
    async initProjectTasks() {
      const t = log.time('viewBoard.shared.initProjectTasks');
      const tasksMap = globalStore.getters['tasksModel/tasksByIds'];

      const boardTasks = [];

      const { tasksWBSCodes } = { tasksWBSCodes: {} };
      const tasksCustomValues = globalStore.getters['columns/getValuesMapByProjectId'](this.projectId);

      Object.keys(tasksMap).forEach(taskId => {
        const task = tasksMap[taskId];

        if (this.shouldDisplayTaskOnBoard(task)) {
          const preparedTask = mapTaskForSharedBoard({
            task,
            visibleOnBoard: true,
            tasksMap,
            projectResources: this.projectResources,
            customFields: globalStore.getters['board/customFields'],
            customValues: tasksCustomValues,
            wbsCode: tasksWBSCodes[task.id],
            colors: globalStore.getters['teamSetting/colors'].data,
            groupingField: globalStore.getters['board/groupingFieldInfo'],
            statusField: globalStore.getters['board/getField'](false, 'status'),
            priorityField: globalStore.getters['board/getField'](false, 'priority'),
            dateHelper,
            showOverdue: checkPricingAccess('overdue_tasks') && this.boardConfig.overdue,
          });

          tasksMap[task.id] = preparedTask;
          boardTasks.push(preparedTask.id);
        } else {
          tasksMap[task.id] = mapTaskForSharedBoard({ task });
        }
      });

      this.tasksMap = tasksMap;
      this.boardTasks = boardTasks;
      t.end();
    },
    prepareGroupedTasks() {
      const [
        groupingFieldInfo,
        sortingFieldInfo,
        getResourcesByProjectId,
      ] = [
        globalStore.getters['board/groupingFieldInfo'],
        globalStore.getters['board/sortingFieldInfo'],
        globalStore.getters['resourcesModel/getResourcesByProjectId'],
      ];
      let boardTasks = this.boardTasks.map(id => this.tasksMap[id]);
      const boardColumns = [...groupingFieldInfo.options] || [];
      const columnsMap = new Map();

      if (boardColumns.length) {
        boardColumns.reduce((acc, column) => {
          acc.set(column.id, {
            ...column,
            tasks: [],
            totalHeight: 0,
            sharedView: true,
          });

          return acc;
        }, columnsMap);
      }

      boardTasks = boardTasks.filter(this.filterTasksCallback);

      boardTasks.sort(compareTasks({
        ...sortingFieldInfo,
        resources: getResourcesByProjectId(this.projectId),
      }));

      this.showFilterStub = !boardTasks.length;

      if (columnsMap) {
        boardTasks.forEach(task => {
          const taskOpts = taskToOptions({ task, groupingFieldInfo });

          taskOpts.forEach(opt => {
            let taskObj = { ...task };
            const customSort = [...this.customSort];
            const column = columnsMap.get(opt);

            // тут идет наполнения колонок тасками
            // идет уставнока position для каждой таски

            if (column && customSort.length > 0 && customSort.find(item => +item.groupKey === column.id)) {
              const group = customSort.find(item => +item.groupKey === column.id);
              const itemExist = group.tasks.find(item => item && item.taskId === taskObj.id);

              if (itemExist) {
                taskObj = { ...taskObj, position: itemExist.position };
              }
            }

            if (column) {
              taskObj.taskHeight = this.calculateTaskHeight(taskObj.id);
              taskObj.offsetTop = column.totalHeight + (column.tasks.length ? 8 : 0);
              column.totalHeight += taskObj.taskHeight + (column.tasks.length ? 8 : 0);
              column.tasks.push(taskObj);
            }
          });
        });
      }
      const newColumns = Array.from(columnsMap, ([key, value]) => value);

      // если есть кастомая сортировка, то сортируем таски в каждой колонке
      if (this.customSort.length > 0 && !this.sortingFieldInfo.id) {
        newColumns.forEach(column => {
          if (column.tasks.length && this.customSort.find(item => +item.groupKey === column.id)) {
            column.tasks.sort(compareTasks({
              key: 'custom',
              resources: this.getResourcesByProjectId(this.projectId),
            }));

            column.totalHeight = 0;
            column.tasks.forEach((task, i) => {
              task.offsetTop = column.totalHeight + (i ? 8 : 0);
              column.totalHeight += task.taskHeight + (i ? 8 : 0);
            });
          }
        });
      }

      this.columns = newColumns;
    },
    shouldDisplayTaskOnBoard(task) {
      return ['task'].includes(task.type);
    },
    calculateTaskHeight(taskId) {
      let taskHeight = 146; // минимальная высота карточки

      if (!['status', 'priority'].includes(globalStore.getters['board/groupingFieldInfo'].key)) {
        taskHeight += 32;
      }

      if (this.tasksMap[this.tasksMap[taskId]?.parent].parent) {
        taskHeight += 18;
      }

      return taskHeight;
    },
    getTasksMap() {
      return this.tasksMap;
    },
    getProjectName() {
      return globalStore.getters['headerView/publicUrlInfo'].projectName;
    },
    getCalendarConfig() {
      return this.calendarConfig;
    },
    /*
     * Как Board.vue для color/start/end: без static_fields — disabled.
     * Дополнительно: если в taskFields с бэка writable !== 1 — тоже disabled (узкий случай API).
     */
    isFieldDisabledForSharedBoard(key) {
      const ganttId = globalStore.getters['board/projectId'];
      if (!ganttId) return true;
      if (!['color', 'start_date', 'end_date'].includes(key)) return false;

      if (!rights.project.hasRight(ganttId, 'static_fields')) {
        return true;
      }

      const boardKeyByUi = { start_date: 'startDate', end_date: 'endDate', color: 'color' };
      const field = (globalStore.getters['board/allFields'] || []).find(
        f => !f.isCustom && f.key === boardKeyByUi[key],
      );

      return !!(field && Number(field.writable) !== 1);
    },
  },
};
</script>

<style module src="./less/sharedBoardPage.less"></style>
